import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Camera, Image as ImageIcon } from "lucide-react";
import api, { fileUrl } from "@/lib/api";

export default function VCheckPhotoBlock({ field, photos, projectId, onChanged, missing }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const min = field.min_photos || 1;
  const arr = photos || [];

  const onFiles = async (files) => {
    setUploading(true);
    try {
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        await api.post(`/projects/${projectId}/vcheck-wp/photos/${field.key}/upload`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} Foto${files.length === 1 ? "" : "s"} hochgeladen`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Foto-Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (photoId) => {
    if (!window.confirm("Foto wirklich löschen?")) return;
    try {
      await api.post(`/projects/${projectId}/vcheck-wp/photos/${field.key}/delete`, { photo_id: photoId });
      toast.success("Foto gelöscht");
      onChanged?.();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <div className={`rounded-lg border p-3 space-y-2 ${
      arr.length >= min ? "bg-emerald-500/[0.04] border-emerald-500/25"
      : missing ? "bg-red-500/[0.06] border-red-500/40"
      : "bg-yellow-500/[0.05] border-yellow-500/25"
    }`} data-testid={`vcheck-photo-${field.key}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-zinc-100">
            <ImageIcon size={13} /> {field.label}
            <span className={`text-[10.5px] font-mono px-1.5 py-0.5 rounded ${
              arr.length >= min ? "bg-emerald-500/20 text-emerald-300" : "bg-yellow-500/20 text-yellow-300"
            }`}>{arr.length} / {min}</span>
          </div>
          {field.hint && <div className="text-[10.5px] text-zinc-500 mt-0.5">{field.hint}</div>}
        </div>
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
                className="h-8 px-3 rounded-md bg-emerald-600 hover:bg-emerald-500 text-zinc-950 text-[12px] font-bold inline-flex items-center gap-1 disabled:opacity-50"
                data-testid={`vcheck-photo-${field.key}-upload`}>
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
          Foto{arr.length > 0 ? " +" : " hochladen"}
        </button>
        <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple className="hidden"
               onChange={(e) => { onFiles([...e.target.files]); e.target.value = ""; }} />
      </div>
      {arr.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {arr.map((p) => (
            <div key={p.id} className="relative group rounded overflow-hidden bg-zinc-900 border border-zinc-800">
              <img src={fileUrl(p.path)} alt={p.name} className="w-20 h-20 object-cover" loading="lazy" />
              <button type="button" onClick={() => remove(p.id)}
                      className="absolute top-0.5 right-0.5 bg-red-500/80 hover:bg-red-500 text-white rounded p-0.5 opacity-0 group-hover:opacity-100"
                      data-testid={`vcheck-photo-del-${p.id}`}>
                <Trash2 size={10} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
